import api, { getAuthHeaders, dbg } from './_client';
import { generateUUID } from '../utils/uuid';
import { DEFAULT_ROOM_CONFIG, createDefaultRoomConfig } from '../constants/defaults';
import { logger as debugLogger } from '../utils/logger';
import { SCREENSHOT_VERSION } from '../utils/roomScreenshot';

// Shared transform: backend room format → frontend format
function transformRoomResponse (backendData, fallbackRoomId = null) {
  return {
    id: backendData.id || fallbackRoomId,
    name: backendData.name || '',
    slug: backendData.slug || '',
    isOwner: backendData.isOwner !== undefined ? backendData.isOwner : true,
    hasPassword: !!backendData.hasPassword,
    passwordRequired: !!backendData.passwordRequired,
    publishedId: backendData.publishedId || null,
    publishedAt: backendData.publishedAt || null,
    roomConfig: {
      scaleX: backendData.config?.dimensions?.width || DEFAULT_ROOM_CONFIG.scaleX,
      scaleZ: backendData.config?.dimensions?.depth || DEFAULT_ROOM_CONFIG.scaleZ,
      height: backendData.config?.dimensions?.height || DEFAULT_ROOM_CONFIG.height,
      materialType: backendData.metadata?.materialType || DEFAULT_ROOM_CONFIG.materialType,
      materialId: backendData.metadata?.materialId || DEFAULT_ROOM_CONFIG.materialId,
      materialOptions: backendData.metadata?.materialOptions || {},
      decorations: backendData.metadata?.decorations || [],
      lightIntensity: backendData.metadata?.lightIntensity ?? DEFAULT_ROOM_CONFIG.lightIntensity,
      roomLiningVisible: backendData.metadata?.roomLiningVisible ?? DEFAULT_ROOM_CONFIG.roomLiningVisible,
      shaderOptions: backendData.metadata?.shaderOptions || {}
    },
    placedModels: (backendData.placedModels || []).map(model => ({
      id: model.id,
      name: model.metadata?.name || '',
      url: model.metadata?.url || '',
      position: [
        model.position?.x || 0,
        model.position?.y || 0,
        model.position?.z || 0
      ],
      rotation: [
        model.rotation?.x || 0,
        model.rotation?.y || 0,
        model.rotation?.z || 0
      ],
      scale: [
        model.scale?.x || 1,
        model.scale?.y || 1,
        model.scale?.z || 1
      ],
      hue: model.metadata?.hue,
      roughness: model.metadata?.roughness,
      assetId: model.assetId,
      category: model.metadata?.category,
      music: model.metadata?.music,
      musicLoop: model.metadata?.musicLoop,
      link: model.metadata?.link,
      hoverText: model.metadata?.hoverText,
      type: model.metadata?.type || null,
      avatarId: model.metadata?.avatarId || null,
      avatarConfig: model.metadata?.avatarConfig || null
    })),
    tags: backendData.tags || [],
    listedInDirectory: backendData.listedInDirectory !== false,
    creatorUsername: backendData.creatorUsername || null,
    creatorDisplayName: backendData.creatorDisplayName || null,
    createdAt: backendData.createdAt,
    updatedAt: backendData.updatedAt,
    expiresAt: backendData.expiresAt || null,
    screenshotUrl: backendData.screenshotUrl || null,
    screenshotGeneratedAt: backendData.screenshotGeneratedAt || null
  }
}

const roomApi = {
  // Get room data by ID
  getRoom: async (roomId, userInfo = null) => {
    try {
      const authHeaders = await getAuthHeaders(userInfo);

      // Get session ID for guest room ownership verification
      let sessionId;
      try {
        const { useSessionStore } = require('../store');
        const store = useSessionStore.getState();
        sessionId = store.currentSessionId || store.getOrCreateSessionId();
      } catch (error) {
        console.warn('Could not get session ID:', error);
        sessionId = null;
      }

      const response = await api.get(`/rooms/${roomId}`, {
        headers: {
          ...authHeaders,
          ...(sessionId && { 'x-session-id': sessionId })
        }
      });
      const backendData = response.data.data || response.data; // Handle both wrapper and direct formats

      // If password-protected and not owner, return limited data
      if (backendData.passwordRequired) {
        return {
          id: backendData.id,
          name: backendData.name || '',
          slug: backendData.slug || '',
          isOwner: false,
          passwordRequired: true
        };
      }

      // Transform backend format to frontend format
      dbg.log('🔍 API getRoom - Backend data structure:', {
        hasMetadata: !!backendData.metadata,
        metadataKeys: backendData.metadata ? Object.keys(backendData.metadata) : [],
        materialType: backendData.metadata?.materialType,
        materialId: backendData.metadata?.materialId,
        materialIdType: typeof backendData.metadata?.materialId,
        rawMetadata: backendData.metadata
      });

      const transformedData = transformRoomResponse(backendData, roomId);

      dbg.log('Loaded room data from backend:', transformedData);
      return transformedData;
    } catch (error) {
      if (error.response?.status === 404) {
        // Room not found, silently return default room structure (expected for new rooms)
        dbg.log(`Creating new room with ID: ${roomId}`);
        return {
          id: roomId,
          isOwner: true, // New rooms are owned by the creator
          roomConfig: createDefaultRoomConfig(),
          placedModels: [],
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };
      }
      // Only log unexpected errors
      console.error('Unexpected error loading room:', error);
      throw error;
    }
  },

  // Save room data (upsert - update if exists, create if not)
  saveRoom: async (roomId, roomData, userInfo = null) => {
    // Get user and session information
    let userId, sessionId;

    if (userInfo && userInfo.isSignedIn && userInfo.user) {
      userId = userInfo.user.id;
      dbg.log('Using authenticated user ID for room save:', userId);
    } else {
      userId = 'guest';
    }

    // Always get session ID for continuity
    try {
      const { useSessionStore } = require('../store');
      const store = useSessionStore.getState();
      sessionId = store.currentSessionId || store.getOrCreateSessionId();
    } catch (error) {
      console.warn('Could not get session ID:', error);
      sessionId = 'guest';
    }

    dbg.log('Saving room with:', { userId, sessionId, roomId });

    // Get auth headers once upfront so they're available for both the existence
    // check GET and the upsert PUT/POST. Without auth on the GET, the backend's
    // security-hardened enforceRoomAccess can return 403 and abort the entire save.
    const authHeaders = await getAuthHeaders(userInfo);

    try {
      // First check if room exists
      const existsResponse = await api.get(`/rooms/${roomId}`, {
        headers: {
          ...authHeaders,
          'x-user-id': userId,
          'x-session-id': sessionId
        }
      }).catch(err => {
        if (err.response?.status === 404) {
          return null; // Room doesn't exist
        }
        if (err.response?.status === 403) {
          // Room exists but GET returned forbidden — unusual if auth is correct, but
          // treat as "exists" so we can attempt the PUT with full auth headers.
          console.warn('⚠️ GET /rooms returned 403 - treating as existing room, attempting PUT');
          return 'exists_forbidden';
        }
        throw err; // Re-throw other errors
      });

      if (existsResponse) {
        // Room exists, update it
        dbg.log(`Room ${roomId} exists, updating...`);
        const updatePayload = {
          config: {
            dimensions: {
              width: roomData.roomConfig.scaleX || DEFAULT_ROOM_CONFIG.scaleX,
              height: roomData.roomConfig.height || DEFAULT_ROOM_CONFIG.height,
              depth: roomData.roomConfig.scaleZ || DEFAULT_ROOM_CONFIG.scaleZ
            },
            lighting: {}
          },
          placedModels: roomData.placedModels.map(model => ({
            id: model.id,
            assetId: model.assetId || '',
            position: {
              x: model.position[0] || 0,
              y: model.position[1] || 0,
              z: model.position[2] || 0
            },
            rotation: {
              x: model.rotation[0] || 0,
              y: model.rotation[1] || 0,
              z: model.rotation[2] || 0
            },
            scale: {
              x: model.scale[0] || 1,
              y: model.scale[1] || 1,
              z: model.scale[2] || 1
            },
            metadata: {
              name: model.name || '',
              url: model.url || '',
              hue: model.hue,
              roughness: model.roughness,
              music: model.music,
              musicLoop: model.musicLoop,
              link: model.link,
              hoverText: model.hoverText,
              category: model.category || '',
              type: model.type || null,
              avatarId: model.avatarId || null,
              avatarConfig: model.avatarConfig || null
            }
          })),
          metadata: {
            materialType: roomData.roomConfig.materialType || DEFAULT_ROOM_CONFIG.materialType,
            materialId: roomData.roomConfig.materialId || DEFAULT_ROOM_CONFIG.materialId,
            materialOptions: roomData.roomConfig.materialOptions || {},
            decorations: roomData.roomConfig.decorations || [],
            lightIntensity: roomData.roomConfig.lightIntensity ?? DEFAULT_ROOM_CONFIG.lightIntensity,
            roomLiningVisible: roomData.roomConfig.roomLiningVisible ?? DEFAULT_ROOM_CONFIG.roomLiningVisible,
            shaderOptions: roomData.roomConfig.shaderOptions || {}
          }
        };

        const authHeaders = await getAuthHeaders({ isSignedIn: true, user: { id: userId } });
        const response = await api.put(`/rooms/${roomId}`, updatePayload, {
          headers: {
            ...authHeaders,
            'x-user-id': userId,
            'x-session-id': sessionId
          }
        });
        dbg.log(`Successfully updated room: ${roomId}`);

        // Track this room ID in the session store
        try {
          const { useSessionStore } = require('../store');
          useSessionStore.getState().addUserRoom(roomId);
        } catch (err) {
          console.warn('Could not track room ID:', err);
        }

        return response.data;
      } else {
        // Room doesn't exist, create it
        dbg.log(`Room ${roomId} doesn't exist, creating new room...`);
        const createPayload = {
          id: roomId,
          name: `Room ${roomId.slice(0, 8)}`,
          description: '',
          config: {
            dimensions: {
              width: roomData.roomConfig.scaleX || DEFAULT_ROOM_CONFIG.scaleX,
              height: roomData.roomConfig.height || DEFAULT_ROOM_CONFIG.height,
              depth: roomData.roomConfig.scaleZ || DEFAULT_ROOM_CONFIG.scaleZ
            },
            lighting: {}
          },
          placedModels: roomData.placedModels.map(model => ({
            id: model.id,
            assetId: model.assetId || '',
            position: {
              x: model.position[0] || 0,
              y: model.position[1] || 0,
              z: model.position[2] || 0
            },
            rotation: {
              x: model.rotation[0] || 0,
              y: model.rotation[1] || 0,
              z: model.rotation[2] || 0
            },
            scale: {
              x: model.scale[0] || 1,
              y: model.scale[1] || 1,
              z: model.scale[2] || 1
            },
            metadata: {
              name: model.name || '',
              url: model.url || '',
              hue: model.hue,
              roughness: model.roughness,
              music: model.music,
              musicLoop: model.musicLoop,
              link: model.link,
              hoverText: model.hoverText,
              category: model.category || '',
              type: model.type || null,
              avatarId: model.avatarId || null,
              avatarConfig: model.avatarConfig || null
            }
          })),
          metadata: {
            materialType: roomData.roomConfig.materialType || DEFAULT_ROOM_CONFIG.materialType,
            materialId: roomData.roomConfig.materialId || DEFAULT_ROOM_CONFIG.materialId,
            materialOptions: roomData.roomConfig.materialOptions || {},
            decorations: roomData.roomConfig.decorations || [],
            lightIntensity: roomData.roomConfig.lightIntensity ?? DEFAULT_ROOM_CONFIG.lightIntensity,
            roomLiningVisible: roomData.roomConfig.roomLiningVisible ?? DEFAULT_ROOM_CONFIG.roomLiningVisible,
            shaderOptions: roomData.roomConfig.shaderOptions || {}
          },
          tags: []
        };

        const authHeaders = await getAuthHeaders({ isSignedIn: true, user: { id: userId } });
        const createResponse = await api.post('/rooms', createPayload, {
          headers: {
            ...authHeaders,
            'x-user-id': userId,
            'x-session-id': sessionId
          }
        });
        dbg.log(`Successfully created room: ${roomId}`);

        // Track this room ID in the session store
        try {
          const { useSessionStore } = require('../store');
          useSessionStore.getState().addUserRoom(roomId);
        } catch (err) {
          console.warn('Could not track room ID:', err);
        }

        return createResponse.data;
      }
    } catch (error) {
      console.error('Error in saveRoom:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      if (error.response?.status === 400) {
        console.error('400 Bad Request - Backend validation error:', error.response?.data?.error);
        console.error('Failed for room:', roomId);
        console.error('PlacedModels count:', roomData?.placedModels?.length || 0);
      }
      throw error;
    }
  },

  // Create new room
  createRoom: async (roomData = {}, userInfo = null) => {
    // Determine userId and sessionId from userInfo
    let userId;
    let sessionId;

    if (userInfo?.isSignedIn && userInfo?.user) {
      // Authenticated user
      userId = userInfo.user.id;
      dbg.log('Creating room for authenticated user:', userId);
    } else {
      // Guest user
      userId = 'guest';
      dbg.log('Creating room for guest user');
    }

    // Get session ID
    try {
      const { useSessionStore } = require('../store');
      const store = useSessionStore.getState();
      sessionId = store.currentSessionId || store.getOrCreateSessionId();
    } catch (error) {
      console.warn('Could not get session ID:', error);
      sessionId = 'guest';
    }

    // Use provided room ID or generate a new one
    const roomId = roomData.id || generateUUID();
    const payload = {
      id: roomId,
      name: roomData.name || `Room ${roomId.slice(0, 8)}`,
      description: roomData.description || '',
      config: {
          dimensions: {
            width: roomData.roomConfig?.scaleX || DEFAULT_ROOM_CONFIG.scaleX,
            height: roomData.roomConfig?.height || DEFAULT_ROOM_CONFIG.height,
            depth: roomData.roomConfig?.scaleZ || DEFAULT_ROOM_CONFIG.scaleZ
          },
          lighting: {}
        },
        placedModels: (roomData.placedModels || []).map(model => ({
          id: model.id || generateUUID(),
          assetId: model.assetId || '',
          position: {
            x: model.position?.[0] || 0,
            y: model.position?.[1] || 0,
            z: model.position?.[2] || 0
          },
          rotation: {
            x: model.rotation?.[0] || 0,
            y: model.rotation?.[1] || 0,
            z: model.rotation?.[2] || 0
          },
          scale: {
            x: model.scale?.[0] || 1,
            y: model.scale?.[1] || 1,
            z: model.scale?.[2] || 1
          },
          metadata: {
            name: model.name || '',
            url: model.url || '',
            hue: model.hue,
            roughness: model.roughness,
            music: model.music,
            musicLoop: model.musicLoop,
            link: model.link,
            hoverText: model.hoverText,
            category: model.category || '',
            type: model.type || null,
            avatarId: model.avatarId || null,
            avatarConfig: model.avatarConfig || null
          }
        })),
        metadata: {
          materialType: roomData.roomConfig?.materialType || DEFAULT_ROOM_CONFIG.materialType,
          materialId: roomData.roomConfig?.materialId || DEFAULT_ROOM_CONFIG.materialId,
          materialOptions: roomData.roomConfig?.materialOptions || {},
          decorations: roomData.roomConfig?.decorations || [],
          shaderOptions: roomData.roomConfig?.shaderOptions || {}
        },
        tags: []
    };

    try {
      dbg.log('Create room payload:', {
        id: roomId,
        userId,
        sessionId,
        name: payload.name
      });
      const authHeaders = await getAuthHeaders(userInfo);
      const response = await api.post('/rooms', payload, {
        headers: {
          ...authHeaders,
          'x-session-id': sessionId,
          'x-user-id': userId
        }
      });
      const responseData = response.data;

      dbg.log('✅ Room created successfully:', {
        roomId,
        responseStatus: responseData?.success,
        responseId: responseData?.data?.id || responseData?.id,
        userId,
        sessionId
      });

      // Verify the room is immediately queryable (with retry logic)
      if (userId && userId !== 'guest') {
        dbg.log('🔍 Verifying room is queryable after creation...');
        let verified = false;
        for (let attempt = 1; attempt <= 3; attempt++) {
          try {
            await new Promise(resolve => setTimeout(resolve, 100 * attempt)); // Exponential backoff
            const verifyResponse = await api.get(`/users/${userId}/rooms`, {
              headers: {
                ...authHeaders,
                'x-user-id': userId,
                'x-session-id': sessionId
              }
            });
            const verifyRooms = verifyResponse.data?.data?.rooms || verifyResponse.data?.rooms || [];
            const foundRoom = verifyRooms.find(r => r.id === roomId);
            if (foundRoom) {
              dbg.log(`✅ Verification successful on attempt ${attempt}: Room is queryable`);
              verified = true;
              break;
            } else {
              dbg.log(`⚠️ Verification attempt ${attempt}: Room not yet in list (found ${verifyRooms.length} rooms)`);
              dbg.log(`   Room IDs returned by backend: ${verifyRooms.map(r => r.id).join(', ')}`);
              dbg.log(`   Searching for: ${roomId}`);
              dbg.log(`   Full response keys: ${Object.keys(verifyResponse.data || {}).join(', ')}`);
            }
          } catch (verifyError) {
            console.warn(`⚠️ Verification attempt ${attempt} failed:`, verifyError.message);
          }
        }
        if (!verified) {
          console.error('❌ Room created but not immediately queryable');
          console.error('   Created room ID:', roomId);
          console.error('   User ID:', userId);
          console.error('   ISSUE: Backend does not return newly created rooms in /users/{userId}/rooms endpoint');
        }
      }

      // Return the full room data from backend (includes generated name, slug, etc.)
      // Backend returns: { success: true, data: { id, name, slug, userId, ... } }
      if (responseData.success && responseData.data) {
        return responseData.data;
      }

      // Fallback for legacy response format
      return {
        id: responseData.data?.id || responseData.id || roomId,
        ...responseData.data,
        ...responseData
      };
    } catch (error) {
      console.error('Failed to create room:', error);
      console.error('Error details:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message,
        config: error.config
      });
      console.error('Request payload that failed:', payload);
      console.error('Request headers:', {
        'x-session-id': sessionId,
        'x-user-id': userId
      });
      throw error;
    }
  },

  // Get user's rooms (guest session or authenticated user)
  getUserRooms: async (userInfo = null) => {
    let userId;
    let sessionId;

    // SECURITY: Only allow authenticated users to fetch rooms
    if (!userInfo || !userInfo.isSignedIn || !userInfo.user) {
      console.warn('getUserRooms called without authenticated user - access denied');
      return {
        success: false,
        data: {
          rooms: [],
          total: 0,
          limit: 0
        },
        error: 'Authentication required'
      };
    }

    userId = userInfo.user.id;
    dbg.log('Using authenticated Clerk user ID:', userId);

    // Always get session ID - backend uses this to find guest rooms created before auth
    try {
      const { useSessionStore } = require('../store');
      const store = useSessionStore.getState();
      sessionId = store.currentSessionId || store.getOrCreateSessionId();
      dbg.log('Using session ID for guest room access:', sessionId);
    } catch (error) {
      console.warn('Could not get session ID:', error);
      sessionId = 'guest';
    }

    try {
      // For authenticated users, fetch rooms that match either:
      // 1. Their authenticated user ID
      // 2. Their session ID (for guest rooms created before login)
      // The backend handles this automatically by checking both userId and sessionId
      if (userId && userId !== 'guest') {
        dbg.log(`Fetching rooms for authenticated user: ${userId} with session: ${sessionId}`);

        let allUserRooms = [];

        try {
          // Use the correct user rooms endpoint: /users/{userId}/rooms
          // This endpoint properly checks both userId AND sessionId for ownership
          const directResponse = await api.get(`/users/${userId}/rooms`, {
            headers: {
              'x-user-id': userId,
              'x-session-id': sessionId
            }
          });

          dbg.log('API response:', directResponse.data);
          dbg.log('API response structure:', {
            hasData: !!directResponse.data,
            dataKeys: directResponse.data ? Object.keys(directResponse.data) : [],
            dataDataKeys: directResponse.data?.data ? Object.keys(directResponse.data.data) : []
          });

          // Extract rooms from response - structure is { success: true, data: { rooms: [...], total, returned, limit } }
          const responseData = directResponse.data?.data || directResponse.data || {};

          dbg.log('DEBUG: Response structure:', {
            fullResponse: directResponse.data,
            responseDataAsData: directResponse.data?.data,
            responseDataDirect: directResponse.data,
            dataKeys: responseData ? Object.keys(responseData) : []
          });

          const directRooms = Array.isArray(responseData.rooms) ? responseData.rooms : (Array.isArray(responseData) ? responseData : []);

          dbg.log(`Extracted ${directRooms.length} rooms from response`, {
            responseDataKeys: Object.keys(responseData),
            isArray: Array.isArray(directRooms),
            firstRoom: directRooms[0],
            firstRoomId: directRooms[0]?.id || directRooms[0]?.roomId,
            allRoomIds: directRooms.map(r => r?.id || r?.roomId)
          });

          // Backend may not include isOwner flag in list results, so calculate it
          // A room is owned if userId matches OR sessionId matches
          // IMPORTANT: Transform backend format to frontend format (match getRoom() format)
          allUserRooms = directRooms.map(room => {
            // Calculate ownership: user owns if userId matches OR sessionId matches
            const isOwner = room.isOwner !== undefined ? room.isOwner :
                           (room.userId === userId || (sessionId && room.sessionId === sessionId));

            // Transform backend format (config + metadata) to frontend format (roomConfig)
            return {
              id: room.id,
              name: room.name,
              slug: room.slug,
              isOwner,
              isPublic: room.isPublic,
              validationStatus: room.validationStatus || 'draft',
              tags: room.tags || [],
              listedInDirectory: room.listedInDirectory !== false,
              createdAt: room.createdAt,
              updatedAt: room.updatedAt,
              userId: room.userId,
              sessionId: room.sessionId,
              roomConfig: {
                scaleX: room.config?.dimensions?.width || DEFAULT_ROOM_CONFIG.scaleX,
                scaleZ: room.config?.dimensions?.depth || DEFAULT_ROOM_CONFIG.scaleZ,
                height: room.config?.dimensions?.height || DEFAULT_ROOM_CONFIG.height,
                materialType: room.metadata?.materialType || DEFAULT_ROOM_CONFIG.materialType,
                materialId: room.metadata?.materialId || DEFAULT_ROOM_CONFIG.materialId,
                materialOptions: room.metadata?.materialOptions || {},
                decorations: room.metadata?.decorations || [],
                shaderOptions: room.metadata?.shaderOptions || {}
              },
              placedModels: (room.placedModels || []).map(model => ({
                id: model.id,
                name: model.metadata?.name || '',
                url: model.metadata?.url || '',
                position: [
                  model.position?.x || 0,
                  model.position?.y || 0,
                  model.position?.z || 0
                ],
                rotation: [
                  model.rotation?.x || 0,
                  model.rotation?.y || 0,
                  model.rotation?.z || 0
                ],
                scale: [
                  model.scale?.x || 1,
                  model.scale?.y || 1,
                  model.scale?.z || 1
                ],
                hue: model.metadata?.hue,
                roughness: model.metadata?.roughness,
                assetId: model.assetId,
                category: model.metadata?.category,
                music: model.metadata?.music,
                musicLoop: model.metadata?.musicLoop,
                link: model.metadata?.link,
                hoverText: model.metadata?.hoverText,
                type: model.metadata?.type || null,
                avatarId: model.metadata?.avatarId || null,
                avatarConfig: model.metadata?.avatarConfig || null
              }))
            };
          });

          dbg.log(`Loaded ${allUserRooms.length} rooms (includes guest session rooms)`);
        } catch (directError) {
          console.warn('API query failed:', directError.message);
        }

        // Don't fallback to tracked room IDs - those are guest sessions that don't belong to the authenticated user
        // Only show rooms that the backend explicitly returns as owned by this user
        if (allUserRooms.length === 0) {
          dbg.log('No authenticated rooms found for user:', userId);
        }

        // Clear tracked room IDs now that user is authenticated
        // Tracked rooms are guest session rooms which are no longer relevant
        try {
          const { useSessionStore } = require('../store');
          useSessionStore.setState({ userRoomIds: [] });
          dbg.log('Cleared tracked room IDs on authentication');
        } catch (error) {
          console.warn('Could not clear tracked room IDs:', error);
        }

        // Return in the expected format
        return {
          success: true,
          data: {
            rooms: allUserRooms,
            total: allUserRooms.length,
            limit: 1000
          }
        };
      } else {
        // For guest users, just get guest rooms
        dbg.log(`Fetching rooms for guest user with session: ${sessionId}`);

        const response = await api.get('/rooms', {
          params: { userId: 'guest' },
          headers: {
            'x-user-id': 'guest',
            'x-session-id': sessionId
          }
        });

        dbg.log('Response status:', response.status);
        dbg.log('Response data:', response.data);
        return response.data;
      }
    } catch (error) {
      console.error('Failed to fetch user rooms:', error);
      console.error('Error details:', {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data
      });
      throw error;
    }
  },

  // Check slug availability
  checkSlugAvailable: async (slug, roomId = null) => {
    try {
      const params = { slug };
      if (roomId) {
        params.roomId = roomId;
      }

      dbg.log('[API] Checking slug availability:', params);
      const response = await api.get('/rooms/slug-check', { params });

      const result = response.data.data || response.data;
      dbg.log('[API] Slug availability result:', result);
      return result;
    } catch (error) {
      console.error('[API] Failed to check slug availability:', error);
      throw error;
    }
  },

  // Get room by slug (globally unique)
  getRoomBySlug: async (slug, userInfo = null) => {
    try {
      dbg.log(`Fetching room by slug: ${slug}`);
      const authHeaders = await getAuthHeaders(userInfo);

      // Get session ID for guest room ownership verification
      let sessionId;
      try {
        const { useSessionStore } = require('../store');
        const store = useSessionStore.getState();
        sessionId = store.currentSessionId || store.getOrCreateSessionId();
      } catch (error) {
        console.warn('Could not get session ID:', error);
        sessionId = null;
      }

      // Use a placeholder userId since slugs are globally unique now
      // Backend will ignore userId and just lookup by slug
      const response = await api.get(`/users/_/rooms/${slug}`, {
        headers: {
          ...authHeaders,
          ...(sessionId && { 'x-session-id': sessionId })
        }
      });

      // Handle 301 redirect
      if (response.status === 301 || response.data?.redirect) {
        const targetSlug = response.data.targetSlug;
        dbg.log(`[API] Slug redirected: ${slug} → ${targetSlug}, following redirect...`);
        // Recursively fetch the target slug
        return await roomApi.getRoomBySlug(targetSlug, userInfo);
      }

      const roomData = response.data.data || response.data;

      // If password-protected and not owner, return limited data
      if (roomData.passwordRequired) {
        return {
          id: roomData.id,
          name: roomData.name || '',
          slug: roomData.slug || '',
          isOwner: false,
          passwordRequired: true
        };
      }

      const transformedData = transformRoomResponse(roomData);

      dbg.log('Loaded room by slug:', transformedData);
      return transformedData;
    } catch (error) {
      console.error('Failed to get room by slug:', error);
      throw error;
    }
  },

  // Delete room
  deleteRoom: async (roomId, userInfo = null) => {
    try {
      // Get authentication headers
      const authHeaders = await getAuthHeaders(userInfo);

      // Get session ID for guest room ownership verification
      let sessionId;
      try {
        const { useSessionStore } = require('../store');
        const store = useSessionStore.getState();
        sessionId = store.currentSessionId || store.getOrCreateSessionId();
      } catch (error) {
        console.warn('Could not get session ID:', error);
        sessionId = null;
      }

      dbg.log(`Deleting room ${roomId}`, {
        sessionId,
        hasSessionId: !!sessionId,
        authHeaderKeys: Object.keys(authHeaders)
      });

      const headers = {
        ...authHeaders,
        ...(sessionId && { 'x-session-id': sessionId })
      };

      dbg.log('DELETE request headers:', {
        headerKeys: Object.keys(headers),
        hasSessionIdHeader: 'x-session-id' in headers,
        sessionIdValue: headers['x-session-id']
      });

      const response = await api.delete(`/rooms/${roomId}`, { headers });

      dbg.log('Room deleted successfully');
      return response.data;
    } catch (error) {
      console.error('Failed to delete room:', error);
      throw error;
    }
  },

  // Update room (partial update)
  updateRoom: async (roomId, updates, userInfo = null) => {
    try {
      // Get authentication headers
      const authHeaders = await getAuthHeaders(userInfo);

      // Get session ID for guest room ownership verification
      let sessionId;
      try {
        const { useSessionStore } = require('../store');
        const store = useSessionStore.getState();
        sessionId = store.currentSessionId || store.getOrCreateSessionId();
      } catch (error) {
        console.warn('Could not get session ID:', error);
        sessionId = null;
      }

      dbg.log('[API] updateRoom called:', {
        roomId,
        updates,
        updateFields: Object.keys(updates),
        userInfo: userInfo ? {
          isSignedIn: userInfo.isSignedIn,
          userId: userInfo.user?.id
        } : 'null',
        authHeaders: Object.keys(authHeaders),
        sessionId
      });

      debugLogger.log('ROOM_PERSISTENCE', `Updating room ${roomId} with fields: ${Object.keys(updates).join(', ')}`);

      dbg.log('[API] Sending PUT request to:', `/rooms/${roomId}`, 'with body:', updates);
      const response = await api.put(`/rooms/${roomId}`, updates, {
        headers: {
          ...authHeaders,
          ...(sessionId && { 'x-session-id': sessionId })
        }
      });

      dbg.log('[API] updateRoom response:', {
        status: response.status,
        statusText: response.statusText,
        data: response.data
      });

      debugLogger.success('ROOM_PERSISTENCE', `Room ${roomId} updated successfully`);
      return response.data;
    } catch (error) {
      const errorDetails = {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message,
        fullError: error
      };

      console.error('[API] updateRoom failed:', {
        roomId,
        updates,
        error: errorDetails
      });

      debugLogger.error('ROOM_PERSISTENCE', `Failed to update room ${roomId}`, errorDetails);
      throw error;
    }
  },

  // Batch fetch featured rooms by slugs (public, no auth needed)
  getFeaturedRooms: async (slugs) => {
    try {
      const response = await api.post('/rooms/featured', { slugs });
      const data = response.data?.data || response.data;
      return (data.rooms || []).map(room => ({
        id: room.id,
        name: room.name,
        slug: room.slug,
        isOwner: false,
        roomConfig: {
          scaleX: room.config?.dimensions?.width || DEFAULT_ROOM_CONFIG.scaleX,
          scaleZ: room.config?.dimensions?.depth || DEFAULT_ROOM_CONFIG.scaleZ,
          height: room.config?.dimensions?.height || DEFAULT_ROOM_CONFIG.height,
          materialType: room.metadata?.materialType || DEFAULT_ROOM_CONFIG.materialType,
          materialId: room.metadata?.materialId || DEFAULT_ROOM_CONFIG.materialId,
          materialOptions: room.metadata?.materialOptions || {},
          decorations: room.metadata?.decorations || [],
          shaderOptions: room.metadata?.shaderOptions || {}
        },
        placedModels: (room.placedModels || []).map(model => ({
          id: model.id,
          name: model.metadata?.name || '',
          url: model.metadata?.url || '',
          position: [model.position?.x || 0, model.position?.y || 0, model.position?.z || 0],
          rotation: [model.rotation?.x || 0, model.rotation?.y || 0, model.rotation?.z || 0],
          scale: [model.scale?.x || 1, model.scale?.y || 1, model.scale?.z || 1],
          hue: model.metadata?.hue,
          roughness: model.metadata?.roughness,
          assetId: model.assetId,
          category: model.metadata?.category,
          music: model.metadata?.music,
          musicLoop: model.metadata?.musicLoop,
          link: model.metadata?.link,
          hoverText: model.metadata?.hoverText,
          type: model.metadata?.type || null,
          avatarId: model.metadata?.avatarId || null,
          avatarConfig: model.metadata?.avatarConfig || null
        })),
        tags: room.tags || [],
        listedInDirectory: room.listedInDirectory !== false,
        creatorUsername: room.creatorUsername,
        creatorDisplayName: room.creatorDisplayName,
        createdAt: room.createdAt,
        updatedAt: room.updatedAt,
        screenshotUrl: room.screenshotUrl || null,
        screenshotGeneratedAt: room.screenshotGeneratedAt || null
      }));
    } catch (error) {
      console.error('Failed to fetch featured rooms:', error);
      return [];
    }
  },

  // List published rooms (public, no auth needed)
  listPublishedRooms: async ({ tags, search, limit } = {}) => {
    try {
      const params = { showPublished: 'true' };
      if (tags) params.tags = tags;
      if (search) params.search = search;
      if (limit) params.limit = limit;

      const response = await api.get('/rooms', { params });
      const data = response.data.data || response.data;
      return data.rooms || [];
    } catch (error) {
      console.error('Failed to list published rooms:', error);
      throw error;
    }
  },

  // Migrate guest rooms to authenticated user
  migrateGuestRooms: async (userInfo = null) => {
    try {
      // Get authentication headers and session ID
      const authHeaders = await getAuthHeaders(userInfo);

      // Get session ID
      let sessionId;
      try {
        const { useSessionStore } = require('../store');
        const store = useSessionStore.getState();
        sessionId = store.currentSessionId || store.getOrCreateSessionId();
      } catch (error) {
        console.warn('Could not get session ID:', error);
        sessionId = 'guest';
      }

      dbg.log('Migrating guest rooms with session:', sessionId);
      const response = await api.post('/rooms/migrate', {}, {
        headers: {
          ...authHeaders,
          'x-session-id': sessionId
        }
      });

      const result = response.data?.data || response.data;
      dbg.log('Room migration result:', result);
      return result;
    } catch (error) {
      console.error('Failed to migrate guest rooms:', error);
      throw error;
    }
  },

  // Get a fresh presigned GET URL for a specific room asset by assetKey.
  // The S3 bucket is private — public URLs 403. Use this whenever a video needs to play.
  // Uses only the filename (last segment of assetKey) to avoid APIGW path-decoding issues
  // with encoded slashes. Backend reconstructs the full S3 key from roomId + filename.
  getAssetDownloadUrl: async (roomId, assetKey) => {
    try {
      const filename = assetKey.split('/').pop();
      const response = await api.get(`/rooms/${roomId}/assets/${filename}`);
      return response.data?.data?.downloadUrl ?? null;
    } catch (error) {
      console.warn('Could not fetch presigned download URL for asset:', error);
      return null;
    }
  },

  // Get a fresh URL for a specific room video asset by assetKey.
  // Used by thumbnails to recover from expired/migrated video URLs.
  getRoomVideoUrl: async (roomId, assetKey, userInfo = null) => {
    try {
      const authHeaders = await getAuthHeaders(userInfo);
      const response = await api.get(`/rooms/${roomId}/assets`, { headers: authHeaders });
      const raw = response.data?.data ?? response.data;
      const assetList = Array.isArray(raw) ? raw : (raw?.assets ?? []);
      const match = assetList.find(a => a.assetKey === assetKey || a.key === assetKey);
      return match ? (match.downloadUrl || match.publicUrl || match.url || null) : null;
    } catch (error) {
      console.warn('Could not fetch fresh video URL for thumbnail:', error);
      return null;
    }
  },

  // Get public creator profile (no auth needed)
  getCreatorProfile: async (identifier) => {
    try {
      const response = await api.get(`/creators/${encodeURIComponent(identifier)}`);
      const data = response.data?.data || response.data;

      // Transform room data using same pattern as getFeaturedRooms
      const rooms = (data.rooms || []).map(room => ({
        id: room.id,
        name: room.name,
        slug: room.slug,
        isOwner: false,
        roomConfig: {
          scaleX: room.config?.dimensions?.width || DEFAULT_ROOM_CONFIG.scaleX,
          scaleZ: room.config?.dimensions?.depth || DEFAULT_ROOM_CONFIG.scaleZ,
          height: room.config?.dimensions?.height || DEFAULT_ROOM_CONFIG.height,
          materialType: room.metadata?.materialType || DEFAULT_ROOM_CONFIG.materialType,
          materialId: room.metadata?.materialId || DEFAULT_ROOM_CONFIG.materialId,
          materialOptions: room.metadata?.materialOptions || {},
          decorations: room.metadata?.decorations || [],
          shaderOptions: room.metadata?.shaderOptions || {}
        },
        placedModels: (room.placedModels || []).map(model => ({
          id: model.id,
          name: model.metadata?.name || '',
          url: model.metadata?.url || '',
          position: [model.position?.x || 0, model.position?.y || 0, model.position?.z || 0],
          rotation: [model.rotation?.x || 0, model.rotation?.y || 0, model.rotation?.z || 0],
          scale: [model.scale?.x || 1, model.scale?.y || 1, model.scale?.z || 1],
          hue: model.metadata?.hue,
          roughness: model.metadata?.roughness,
          assetId: model.assetId,
          category: model.metadata?.category,
          music: model.metadata?.music,
          musicLoop: model.metadata?.musicLoop,
          link: model.metadata?.link,
          hoverText: model.metadata?.hoverText
        })),
        tags: room.tags || [],
        listedInDirectory: room.listedInDirectory !== false,
        creatorUsername: room.creatorUsername,
        creatorDisplayName: room.creatorDisplayName,
        createdAt: room.createdAt,
        updatedAt: room.updatedAt,
        screenshotUrl: room.screenshotUrl || null,
        screenshotGeneratedAt: room.screenshotGeneratedAt || null
      }));

      return {
        creator: data.creator,
        rooms,
        roomCount: data.roomCount
      };
    } catch (error) {
      if (error.response?.status === 404) {
        return null;
      }
      console.error('Failed to fetch creator profile:', error);
      throw error;
    }
  },

  // Report a room for inappropriate content
  reportRoom: async (roomId, reason, details = '', userInfo = null) => {
    try {
      const authHeaders = await getAuthHeaders(userInfo);

      // Get session ID
      let sessionId;
      try {
        const { useSessionStore } = require('../store');
        const store = useSessionStore.getState();
        sessionId = store.currentSessionId || store.getOrCreateSessionId();
      } catch (error) {
        console.warn('Could not get session ID:', error);
        sessionId = 'anonymous';
      }

      const response = await api.post(`/rooms/${roomId}/report`, {
        reason,
        details
      }, {
        headers: {
          ...authHeaders,
          'x-session-id': sessionId
        }
      });

      return response.data?.data || response.data;
    } catch (error) {
      console.error('Failed to report room:', error);
      throw error;
    }
  },

  // Record a room view (fire-and-forget). The server skips owner/self views and
  // never exposes raw numbers publicly; the client de-dupes per browser/24h
  // before calling this (see PlayerPage). Errors are swallowed — non-critical.
  recordRoomView: async (roomId, userInfo = null) => {
    try {
      const authHeaders = await getAuthHeaders(userInfo);
      let sessionId;
      try {
        const { useSessionStore } = require('../store');
        const store = useSessionStore.getState();
        sessionId = store.currentSessionId || store.getOrCreateSessionId();
      } catch (error) {
        sessionId = 'anonymous';
      }
      await api.post(`/rooms/${roomId}/view`, {}, {
        headers: { ...authHeaders, 'x-session-id': sessionId }
      });
    } catch (error) {
      // View counting is best-effort; never disrupt the viewing experience.
      console.debug('recordRoomView failed (ignored):', error?.message);
    }
  },

  // "Popular this week" — rooms ranked by recent views (server falls back to
  // newest when there's not enough view data). Returns an array of rooms.
  getPopularRooms: async (limit = 12) => {
    try {
      const response = await api.get(`/rooms/popular?limit=${encodeURIComponent(limit)}`);
      const data = response.data?.data || response.data || {};
      return data.rooms || [];
    } catch (error) {
      console.error('Failed to fetch popular rooms:', error);
      return [];
    }
  },

  // Upload room screenshot
  uploadScreenshot: async (roomId, imageData, contentType = 'image/jpeg', userInfo = null) => {
    try {
      const authHeaders = await getAuthHeaders(userInfo);

      // Get session ID
      let sessionId;
      try {
        const { useSessionStore } = require('../store');
        const store = useSessionStore.getState();
        sessionId = store.currentSessionId || store.getOrCreateSessionId();
      } catch (error) {
        console.warn('Could not get session ID:', error);
        sessionId = null;
      }

      dbg.log('[API] Uploading screenshot for room:', roomId);

      const response = await api.post(`/rooms/${roomId}/screenshot`, {
        imageData,
        contentType,
        screenshotVersion: SCREENSHOT_VERSION
      }, {
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
          ...(sessionId && { 'x-session-id': sessionId })
        }
      });

      dbg.log('[API] Screenshot uploaded:', response.data);
      return response.data?.data || response.data;
    } catch (error) {
      console.error('[API] Failed to upload screenshot:', error);
      throw error;
    }
  },

  // Verify room password and get full room data
  verifyRoomPassword: async (roomId, password, userInfo = null) => {
    try {
      const authHeaders = await getAuthHeaders(userInfo);

      let sessionId;
      try {
        const { useSessionStore } = require('../store');
        const store = useSessionStore.getState();
        sessionId = store.currentSessionId || store.getOrCreateSessionId();
      } catch (error) {
        sessionId = null;
      }

      const response = await api.post(`/rooms/${roomId}/verify-password`, { password }, {
        headers: {
          ...authHeaders,
          ...(sessionId && { 'x-session-id': sessionId })
        }
      });

      const backendData = response.data.data || response.data;
      return transformRoomResponse(backendData, roomId);
    } catch (error) {
      console.error('Failed to verify room password:', error);
      throw error;
    }
  }
};


export { roomApi };
